import type React from "react";
import { type RefObject, useEffect, useRef, useState } from "react";
import Select, {
	components,
	type GroupBase,
	type MultiValue,
	type SingleValue,
	type StylesConfig,
	type Theme,
} from "react-select";
import type { Option } from "@/interfaces/Option";
import { cn } from "@/lib/utils";

type Props = {
	options: Option[];
	maxMenuHeight: number;
	placeholder: string;
	value: MultiValue<Option> | SingleValue<Option>;
	onChange: (value: MultiValue<Option> | SingleValue<Option>) => void;
	isTypeList: boolean;
	parentRef?: RefObject<HTMLDivElement | null>;
	isLast?: boolean;
	isCondensed?: boolean;
	isDisabled?: boolean;
	className?: string;
};

const HiddenSelectedBadges = ({ items }: { items: string[] }) => {
	const length = items.length;
	const label = `+ ${length} item${length !== 1 ? "s" : ""}`;

	return (
		<span className="ml-auto bg-gray rounded-[2px] px-1 order-last">
			{label}
		</span>
	);
};

const MultiValueComponent = ({ index, getValue, ...props }: any) => {
	const maxToShow = 1;
	const overflow = getValue()
		.slice(maxToShow)
		.map((x: Option) => x.label);

	return index < maxToShow ? (
		<components.MultiValue {...props} />
	) : index === maxToShow ? (
		<HiddenSelectedBadges items={overflow} />
	) : null;
};

export const Dropdown: React.FC<Props> = ({
	options,
	maxMenuHeight,
	placeholder,
	value,
	onChange,
	isTypeList,
	parentRef,
	isLast,
	isCondensed = false,
	isDisabled = false,
	className,
}) => {
	const [menuPlacement, setMenuPlacement] = useState<"bottom" | "top">(
		"bottom",
	);

	const dropDownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const resizeObserver = new ResizeObserver(() => {
			if (!parentRef?.current || !dropDownRef.current) return;

			const parentRect = parentRef.current.getBoundingClientRect();
			const dropDownRect = dropDownRef.current.getBoundingClientRect();

			const distanceToParentBottom = parentRect.bottom - dropDownRect.bottom;

			if (distanceToParentBottom < 10 && isLast) {
				setMenuPlacement("top");
			} else {
				setMenuPlacement("bottom");
			}
		});

		if (parentRef?.current) resizeObserver.observe(parentRef.current);
		if (dropDownRef.current) resizeObserver.observe(dropDownRef.current);

		return () => resizeObserver.disconnect();
	}, [parentRef, isLast]);

	const theme = (theme: Theme) => ({
		...theme,
		spacing: {
			...theme.spacing,
			baseUnit: 0,
		},
	});

	const colors = {
		primary: "var(--popover)",
		secondary: "var(--dark-background)",
		light: "#dcdcdc",
		medium: "#6a6a6a",
		hover: "var(--filters-border)",
	};

	const customStyles: StylesConfig<Option, true, GroupBase<Option>> = {
		control: (styles, state) => ({
			...styles,
			backgroundColor: colors.primary,
			borderColor: colors.medium,
			cursor: "pointer",
			boxShadow: state.isFocused ? "none" : colors.secondary,
			"&:hover": {
				borderColor: colors.hover,
			},
			margin: 0,
			borderRadius: 6,
			fontSize: 10,
			boxSizing: "border-box",
			minHeight: 18,
			height: "100%",
		}),

		container: (styles) => ({
			...styles,
			width: "100%",
			maxWidth: "100%",
			...(isDisabled && { opacity: 0.5 }),
		}),
		valueContainer: (styles) => ({
			...styles,
			position: isTypeList ? "relative" : "absolute",
			bottom: isTypeList ? 0 : 1,
			left: 0,
			padding: isTypeList ? 1 : 0,
		}),
		indicatorsContainer: (styles) => ({
			...styles,
			position: isTypeList ? "relative" : "absolute",
			bottom: isTypeList ? 0 : -2,
			right: isTypeList ? 0 : 2,
		}),
		placeholder: (styles) => ({
			...styles,
			color: colors.light,
			margin: 0,
			padding: 0,
			paddingLeft: 6,
			display: "inline-block",
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		}),
		input: (styles) => ({
			...styles,
			color: colors.light,
			paddingLeft: 6,
		}),
		indicatorSeparator: (styles) => ({
			...styles,
			backgroundColor: colors.medium,
			marginTop: isTypeList ? 0 : 2,
			height: isTypeList ? "100%" : 16,
		}),
		dropdownIndicator: (styles) => ({
			...styles,
			color: colors.medium,
			"&:hover": {
				color: colors.hover,
			},
			width: isTypeList ? 16 : 14,
		}),
		menu: (styles) => ({
			...styles,
			backgroundColor: colors.primary,
			borderRadius: 6,
			overflow: "visible",
			fontSize: 10,
			marginTop: 2,
			marginBottom: 2,
			padding: 4,
		}),
		option: (styles, { isSelected }) => ({
			...styles,
			color: colors.light,
			backgroundColor: isSelected ? colors.secondary : colors.primary,
			"&:hover": {
				backgroundColor: colors.secondary,
				cursor: "pointer",
			},
			borderRadius: 4,
			paddingInline: isTypeList ? 2 : 2,
			paddingBlock: 1,
			marginBottom: 2,
			fontSize: 10,
		}),
		singleValue: (styles) => ({
			...styles,
			paddingLeft: 6,
			color: colors.light,
		}),
		multiValue: (styles) => ({
			...styles,
			backgroundColor: "#2f2f2f",
		}),
		multiValueLabel: (styles) => ({
			...styles,
			padding: 0,
			fontSize: 10,
			color: colors.light,
		}),
		multiValueRemove: (styles) => ({
			...styles,
			color: colors.light,
			"&:hover": {
				color: colors.hover,
				backgroundColor: "transparent",
			},
			width: 14,
		}),
		noOptionsMessage: (styles) => ({
			...styles,
			color: colors.light,
		}),
	};

	return (
		<div
			ref={dropDownRef}
			className={cn("flex", className)}
			data-testid="dropdown"
		>
			<Select
				hideSelectedOptions={false}
				menuPortalTarget={document.body}
				options={options}
				placeholder={placeholder}
				value={value}
				onChange={(newValue) => onChange(newValue)}
				styles={customStyles}
				theme={theme}
				maxMenuHeight={maxMenuHeight}
				isMulti={isTypeList ? true : undefined}
				isSearchable={isTypeList}
				components={{
					ClearIndicator: () => null,
					...(isTypeList && isCondensed && { MultiValueComponent }),
				}}
				menuPlacement={menuPlacement}
				isDisabled={isDisabled}
			/>
		</div>
	);
};
